import { NuEmailAPIService } from '@/servicehandlers/NuEmailAPIService';
import { prepareEmailBodyHtml } from '@/components/search/email/emailBodyHtml';
import { decodeHtmlEntities } from '@/components/search/email/emailConnectCore';
import { ALL_PEOPLE_MAILBOX_ID, EMAIL_FUTURE } from '@/components/search/email/emailConstants';

// 1 Jul 2026 JCH: Gmail API client for Email.vue / EmailMessage.vue.

const svc = new NuEmailAPIService();
const API_BASE = process.env.VUE_APP_API_URL || ''; // 1 Jul 2026 JCH: empty in dev → proxy

function triggerBlobDownload(blob, filename) {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'attachment';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  }
}

function toIso(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);// API sends RFC-2822 ("Wed, 01 Jul 2026 18:01:52 +0000")
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

function mapMailbox(m) {
  return { id: m.email, name: m.name || m.email, address: m.email, unread: m.unread, total: m.total };
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Plain-text-only messages have no bodyHtml — wrap escaped text so v-html preserves line breaks.
function plainTextToHtml(text) {
  const s = text != null ? String(text) : '';
  if (!s.trim()) return '';
  return `<div class="email-body-plain">${escapeHtml(s)}</div>`;
}

function resolveBodyHtml(m) {
  const html = String((m && (m.bodyHtml || m.body_html)) || '').trim();
  if (html) return html;
  const plain = m && m.bodyPlain != null
    ? String(m.bodyPlain)
    : m && m.body != null ? String(m.body) : '';
  if (plain.trim()) return plainTextToHtml(plain);
  const snippet = m && m.snippet != null ? String(m.snippet) : '';
  if (snippet.trim()) return plainTextToHtml(snippet);
  return '';
}

function mapListItem(m, mailboxId, toLabel = '') {
  return {
    id: m.id,
    mailbox_id: mailboxId,
    subject: decodeHtmlEntities(m.subject || ''),
    from: m.from || '',
    to: toLabel,
    to_address: '',
    received_at: toIso(m.date),
    snippet: decodeHtmlEntities(m.snippet || ''),
    attachment_count: m.hasAttachments ? 1 : 0, // 1 Jul 2026 JCH: list API is boolean, not a count
    connected: false,
    deleted: false,
    read: !!m.read,
  };
}

function mapDetail(m, mailboxId) {
  const attachments = Array.isArray(m.attachments) ? m.attachments : [];
  const sourceBodyHtml = String((m && (m.bodyHtml || m.body_html)) || '').trim();
  return {
    id: m.id,
    mailbox_id: mailboxId,
    subject: m.subject || '',
    from: m.from || '',
    to: m.to || '',
    to_address: m.to || '',
    cc: m.cc || '',
    received_at: toIso(m.date),
    snippet: m.snippet || '',
    body_html: resolveBodyHtml(m),
    has_html_body: !!sourceBodyHtml,
    body: m.bodyPlain || m.body || '',
    attachments: attachments.map((a) => ({
      filename: a.filename,
      content_type: a.mimeType || a.content_type,
      attachmentId: a.attachmentId,
      content_id: a.contentId || a.content_id || a.contentID || '',
      size: a.size,
    })),
    connected: false,
    deleted: false,
  };
}

function attachmentFetchUrl(mailboxId, messageId, attachmentId) {
  return `${API_BASE}/api/gmail/mailboxes/${encodeURIComponent(mailboxId)}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`;
}

function sumMailboxTotals(boxes) {
  return (Array.isArray(boxes) ? boxes : []).reduce(
    (sum, box) => sum + (Number.isFinite(box.total) ? box.total : 0),
    0,
  );
}

function createNuEmailApiClient() {
  const pageTokens = {}; // 1 Jul 2026 JCH: forward-only page tokens per mailbox|pageSize
  let cachedPersonMailboxes = null;

  function invalidateListCache() {
    Object.keys(pageTokens).forEach((k) => { delete pageTokens[k]; });
    cachedPersonMailboxes = null;
  }

  async function personMailboxes() {
    if (cachedPersonMailboxes) return cachedPersonMailboxes;
    const rows = await svc.listMailboxes();
    cachedPersonMailboxes = (Array.isArray(rows) ? rows : []).map(mapMailbox);
    return cachedPersonMailboxes;
  }

  async function listAllPeopleMessages({ page = 1, pageSize = 100, bucketFilter = 'inbox' } = {}) { // 1 Jul 2026 JCH: merge recent mail from every mailbox
    if (
      (bucketFilter === 'connected' && !EMAIL_FUTURE.connectedBucket)
      || (bucketFilter === 'deleted' && !EMAIL_FUTURE.trashBucket) // 1 Jul 2026 JCH: empty until EMAIL_FUTURE flags flip
    ) {
      return { items: [], total: 0, page, pageSize, hasNext: false };
    }
    const boxes = await personMailboxes();
    const perBox = Math.min(page * pageSize, 500);
    const batches = await Promise.all(
      boxes.map(async (box) => {
        const email = box.id;
        const label = box.name || email;
        try {
          const data = await svc.listMessages(email, { maxResults: perBox });
          const messages = data && Array.isArray(data.messages) ? data.messages : [];
          const hasMore = !!(data && data.nextPageToken);
          return {
            items: messages.map((m) => mapListItem(m, email, label)),
            hasMore,
          };
        } catch (e) {
          return { items: [], hasMore: false };
        }
      }),
    );
    const merged = batches
      .flatMap((b) => b.items)
      .sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
    const start = (page - 1) * pageSize;
    const items = merged.slice(start, start + pageSize);
    const hasNext = start + pageSize < merged.length || batches.some((b) => b.hasMore);
    const mailboxTotal = sumMailboxTotals(boxes);
    return {
      items,
      total: mailboxTotal > 0 ? mailboxTotal : merged.length, // 1 Jul 2026 JCH: match dropdown sum, not merge pool size
      page,
      pageSize,
      hasNext,
    };
  }

  return {
    async listMailboxes() {
      return personMailboxes();
    },

    async listMessages({ mailboxId, page = 1, pageSize = 100, bucketFilter = 'inbox' } = {}) {
      if (mailboxId === ALL_PEOPLE_MAILBOX_ID) {
        return listAllPeopleMessages({ page, pageSize, bucketFilter });
      }
      if (
        (bucketFilter === 'connected' && !EMAIL_FUTURE.connectedBucket)
        || (bucketFilter === 'deleted' && !EMAIL_FUTURE.trashBucket) // 1 Jul 2026 JCH: empty until EMAIL_FUTURE flags flip
      ) {
        return { items: [], total: 0, page, pageSize, hasNext: false };
      }
      const key = `${mailboxId}|${pageSize}`;
      if (!pageTokens[key]) pageTokens[key] = [null];
      const pageToken = pageTokens[key][page - 1] != null ? pageTokens[key][page - 1] : null;
      const data = await svc.listMessages(mailboxId, { maxResults: pageSize, pageToken });
      const messages = data && Array.isArray(data.messages) ? data.messages : [];
      const nextPageToken = data && data.nextPageToken ? data.nextPageToken : null;
      if (nextPageToken) pageTokens[key][page] = nextPageToken; // 1 Jul 2026 JCH: hasNext beats approximate totalMessages
      return {
        items: messages.map((m) => mapListItem(m, mailboxId)),
        total: data && typeof data.totalMessages === 'number' ? data.totalMessages : messages.length,
        page,
        pageSize,
        hasNext: !!nextPageToken,
      };
    },

    async getMessage(mailboxId, messageId) {
      const data = await svc.getMessage(mailboxId, messageId);
      if (!data) return null;
      const detail = mapDetail(data, mailboxId);
      detail.body_html = prepareEmailBodyHtml(detail.body_html, {
        mailboxId,
        messageId,
        attachments: detail.attachments,
        apiBase: API_BASE,
      });
      return detail;
    },

    async fetchAttachmentBlob(mailboxId, messageId, att) { // 11 Aug 2026 JCH: user download only; convert handles filing server-side
      const attId = att && att.attachmentId;
      if (!attId) throw new Error('This attachment has no download id.');
      const res = await fetch(attachmentFetchUrl(mailboxId, messageId, attId), { credentials: 'include' });
      if (!res.ok) throw new Error(`Attachment download failed (${res.status}).`);
      return res.blob();
    },

    async downloadAttachment(mailboxId, messageId, att) {
      const blob = await this.fetchAttachmentBlob(mailboxId, messageId, att);
      triggerBlobDownload(blob, att && att.filename);
    },

    async softDeleteMessages(mailboxId, messageIds) {
      const ids = Array.isArray(messageIds) ? messageIds : [];
      const moved = [];
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await svc.trashMessage(mailboxId, id);
        moved.push(id);
      }
      if (moved.length) invalidateListCache();
      return { moved };
    },

    async markMessagesConnected(mailboxId, messageIds) { // 1 Jul 2026 JCH: Gmail has no connected state — filing is app-side
      return { marked: Array.isArray(messageIds) ? messageIds : [] };
    },

    /**
     * 8 Aug 2026 JCH: convert email → firm note on the server.
     * body: { fid, status, staff?, re?, note?, attachments?: [{ attachmentId, filename, mimeType?, description? }] }
     * Pass attachments: [] for note + email.html/txt only (no Gmail file parts).
     */
    async convertToNote(mailboxId, messageId, body) {
      return svc.convertMessage(mailboxId, messageId, body);
    },
  };
}

export function createNuEmailClient() {
  return createNuEmailApiClient();
}
